import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ViewModeProvider } from "./contexts/ViewModeContext";
import { ProtectedRoute, PublicRoute } from "./components/ProtectedRoute";
import DashboardLayout from "./components/layout/DashboardLayout";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import UserManagementPage from "./pages/admin/UserManagementPage";
import MedicineInventoryPage from "./pages/admin/MedicineInventoryPage";
import SettingsPage from "./pages/admin/SettingsPage";
import PharmacyInventoryPage from "./pages/pharmacy/PharmacyInventoryPage";
import DoctorMedicineSearchPage from "./pages/doctor/DoctorMedicineSearchPage";
import PatientsListPage from "./pages/patients/PatientsListPage";
import PatientProfilePage from "./pages/patients/PatientProfilePage";
import ConsultationsListPage from "./pages/consultations/ConsultationsListPage";
import ConsultationDetailPage from "./pages/consultations/ConsultationDetailPage";
import PrescriptionsListPage from "./pages/prescriptions/PrescriptionsListPage";
import PrescriptionEditorPage from "./pages/prescriptions/PrescriptionEditorPage";
import FollowUpsListPage from "./pages/follow-ups/FollowUpsListPage";
import FollowUpDetailPage from "./pages/follow-ups/FollowUpDetailPage";
import MediaLibraryPage from "./pages/admin/MediaLibraryPage";
import AnalyticsDashboardPage from "./pages/admin/AnalyticsDashboardPage";
import KnowledgeBasePage from "./pages/admin/KnowledgeBasePage";
import MedicineUpdatesPage from "./pages/admin/MedicineUpdatesPage";
import PendingReasonsPage from "./pages/admin/PendingReasonsPage";
import SystemMonitorPage from "./pages/admin/SystemMonitorPage";
import OrgManagementPage from "./pages/admin/OrgManagementPage";
import WhatsAppPage from "./pages/admin/WhatsAppPage";
import TaskLoginPage from "./pages/TaskLoginPage";
import MedicineTasksPage from "./pages/tasks/MedicineTasksPage";
import ConsultCardDetailPage from "./pages/consult-cards/ConsultCardDetailPage";
import QueuePage from "./pages/queue/QueuePage";
import SharedMediaPage from "./pages/shared/SharedMediaPage";
import PlaceholderPage from "./pages/PlaceholderPage";
import { Toaster } from "./components/ui/sonner";

function App() {
  return (
    <ViewModeProvider>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route path="/shared/media/:token" element={<SharedMediaPage />} />
          <Route path="/task-login" element={<TaskLoginPage />} />

          {/* Protected Routes */}
          <Route
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<DashboardPage />} />
            
            {/* Shared Patient Routes */}
            <Route
              path="/patients/:patientId"
              element={
                <ProtectedRoute allowedRoles={['admin', 'doctor', 'counsellor']}>
                  <PatientProfilePage />
                </ProtectedRoute>
              }
            />
            
            {/* Shared Consultation Routes */}
            <Route
              path="/consultations/:consultationId"
              element={
                <ProtectedRoute allowedRoles={['admin', 'doctor', 'counsellor']}>
                  <ConsultationDetailPage />
                </ProtectedRoute>
              }
            />
            
            {/* Admin Routes */}
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <UserManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/patients"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <PatientsListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/medicines"
              element={
                <ProtectedRoute allowedRoles={['admin', 'doctor']}>
                  <MedicineInventoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/medicine-updates"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <MedicineUpdatesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/media-library"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <MediaLibraryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AnalyticsDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/system-monitor"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <SystemMonitorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/organisations"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <OrgManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/whatsapp"
              element={
                <ProtectedRoute allowedRoles={['admin', 'doctor', 'counsellor']}>
                  <WhatsAppPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks/medicines"
              element={
                <ProtectedRoute allowedRoles={['admin', 'doctor', 'counsellor']}>
                  <MedicineTasksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/knowledge-base"
              element={
                <ProtectedRoute allowedRoles={['admin', 'doctor']}>
                  <KnowledgeBasePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pending-reasons"
              element={
                <ProtectedRoute allowedRoles={['admin', 'doctor']}>
                  <PendingReasonsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/queue"
              element={
                <ProtectedRoute allowedRoles={['admin', 'doctor', 'counsellor']}>
                  <QueuePage />
                </ProtectedRoute>
              }
            />

            {/* Doctor Routes */}
            <Route
              path="/doctor/patients"
              element={
                <ProtectedRoute allowedRoles={['doctor']}>
                  <PatientsListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/doctor/consultations"
              element={
                <ProtectedRoute allowedRoles={['doctor']}>
                  <ConsultationsListPage />
                </ProtectedRoute>
              }
            />
            <Route path="/doctor/medicines" element={<DoctorMedicineSearchPage />} />
            <Route
              path="/doctor/prescriptions"
              element={
                <ProtectedRoute allowedRoles={['admin', 'doctor']}>
                  <PrescriptionsListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/prescriptions/:prescriptionId/edit"
              element={
                <ProtectedRoute allowedRoles={['admin', 'doctor']}>
                  <PrescriptionEditorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/doctor/follow-ups"
              element={
                <ProtectedRoute allowedRoles={['admin', 'doctor']}>
                  <FollowUpsListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/follow-ups/:followUpId"
              element={
                <ProtectedRoute allowedRoles={['admin', 'doctor', 'counsellor']}>
                  <FollowUpDetailPage />
                </ProtectedRoute>
              }
            />

            {/* Counsellor Routes */}
            <Route
              path="/counsellor/patients"
              element={
                <ProtectedRoute allowedRoles={['counsellor']}>
                  <PatientsListPage />
                </ProtectedRoute>
              }
            />
            <Route path="/counsellor/appointments" element={<PlaceholderPage />} />
            <Route path="/counsellor/consultations" element={<PlaceholderPage />} />

            {/* Pharmacy Routes */}
            <Route path="/pharmacy/inventory" element={<PharmacyInventoryPage />} />
            <Route path="/pharmacy/prescriptions" element={<PlaceholderPage />} />
            <Route path="/pharmacy/orders" element={<PlaceholderPage />} />

            {/* Front Desk Routes */}
            <Route
              path="/front-desk/patients"
              element={
                <ProtectedRoute allowedRoles={['front_desk', 'admin']}>
                  <PatientsListPage />
                </ProtectedRoute>
              }
            />

            {/* Consult Card Detail - accessible by all clinical roles */}
            <Route
              path="/consult-cards/:cardId"
              element={
                <ProtectedRoute allowedRoles={['admin', 'doctor', 'counsellor', 'front_desk']}>
                  <ConsultCardDetailPage />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Default Redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
    </ViewModeProvider>
  );
}

export default App;
